import React, { useState } from "react";
import { motion } from "framer-motion";
import { SectionHeader, cx } from "@/components/site/primitives";
import { fadeUp, stagger } from "@/lib/motion";
import { PEOPLE_TO_FOLLOW } from "@/data";
import { useAuth } from "@/lib/useAuth";
import { toast } from "@/components/ui/sonner";

const spineTone = {
  coral: "from-coral to-[#b85e58]",
  yellow: "from-yellow to-[#e0b164]",
  sage: "from-sage to-[#a9c39d]",
  blue: "from-blue to-[#6b808b]",
};

function FollowButton({ person }) {
  const { getProfile, followUser } = useAuth();
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleFollow = async () => {
    if (following || loading) return;
    setLoading(true);
    try {
      const profileResponse = person.id ? null : await getProfile(person.handle);
      const profile = profileResponse?.data?.profile || profileResponse?.data || profileResponse?.profile || profileResponse;
      const userId = person.id || profile?.id;
      await followUser(userId);
      setFollowing(true);
      toast(`Following @${person.handle}`);
    } catch (error) {
      toast(error.message || `We could not follow @${person.handle}.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleFollow}
      disabled={loading}
      aria-pressed={following}
      className={cx(
        "rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all duration-300 ease-soft",
        following ? "border-line bg-cream text-subtle" : "border-coral/40 text-coral hover:bg-coral/10"
      )}
    >
      {loading ? "Following..." : following ? "Following" : "Follow"}
    </button>
  );
}

export default function PeopleToFollow() {
  return (
    <section className="u-container mt-14 md:mt-20" data-testid="explore-people">
      <SectionHeader eyebrow="Readers & writers like you" title="People You May Like" />
      <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {PEOPLE_TO_FOLLOW.map((person, i) => (
          <motion.div key={person.handle} variants={fadeUp} custom={i} className="card-paper flex flex-col items-center gap-2.5 p-5 text-center">
            <span className={cx("grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br font-display text-lg font-bold text-[#FFF7F0]", spineTone[person.tone])}>
              {person.name[0]}
            </span>
            <div>
              <p className="text-sm font-semibold text-ink">{person.name}</p>
              <p className="text-xs text-subtle">@{person.handle}</p>
            </div>
            <p className="text-[11px] text-subtle">{person.tagline}</p>
            <p className="text-[10px] text-subtle/80">{person.mutual} mutual {person.mutual === 1 ? "community" : "communities"}</p>
            <FollowButton person={person} />
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}
